import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import * as Sentry from "@sentry/react";
import { connectRouter, routerMiddleware } from "connected-react-router";
import { createBrowserHistory } from "history";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import createSagaMiddleware from "redux-saga";

import { authReducer } from "modules/auth";
import { accountsReducer } from "modules/bank-accounts";
import { banksReducer } from "modules/banks";
import {
  cashManagementNotificationReducer,
  cashManagementProviderCustomerConfigReducer,
  cashManagementProviderReducer,
  cashManagementReducer,
} from "modules/cash-management";
import { checkoutBrandingReducer } from "modules/checkout";
import {
  circuitBreakerBlocklistReducer,
  circuitBreakerRiskRulesReducer as circuitBreakerRiskRuleReducer,
  riskOverviewReducer,
} from "modules/circuit-breaker";
import { customerReducer, customersReducer } from "modules/customers";
import { reportsReducer, transactionsReducer } from "modules/transactions";
import { userManagementReducer } from "modules/user-management";
import { userSettingsReducer } from "modules/user-settings";

import { authApi, portalApi } from "../api/rtkApi";
import rootSaga from "../sagas";
import appReducer from "./appSlice";
import notificationsReducer from "./notificationsSlice";
import valuesReducer from "./valuesSlice";

export const history = createBrowserHistory();

const sagaMiddleware = createSagaMiddleware();

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  actionTransformer: (action) => {
    if (action.type === "auth/setAccessTokenData") {
      return {
        ...action,
        payload: {
          ...action.payload,
          accessToken: "<redacted>",
          refreshToken: "<redacted>",
        },
      };
    }

    if (
      action.type.startsWith("__rtkq/") ||
      action.type.startsWith("portalApi/") ||
      action.type.startsWith("authApi/")
    ) {
      return null;
    }

    return action;
  },
});

const appPersistConfig = {
  key: "app",
  storage,
  whitelist: ["environment", "globalFilteredCustomer"],
  blacklist: [],
};

const circuitBreakerRiskRulesPersistConfig = {
  key: "circuitBreakerRiskRules",
  storage,
  whitelist: ["draftsCustomerId", "draftRules"],
  blacklist: [],
};

const rootReducer = combineReducers({
  router: connectRouter(history),
  app: persistReducer(appPersistConfig, appReducer),
  auth: authReducer,
  checkoutBranding: checkoutBrandingReducer,
  transactions: transactionsReducer,
  accounts: accountsReducer,
  users: userManagementReducer,
  values: valuesReducer,
  customers: customersReducer,
  notifications: notificationsReducer,
  banks: banksReducer,
  cashManagement: cashManagementReducer,
  cashManagementProvider: cashManagementProviderReducer,
  cashManagementProviderCustomerConfig: cashManagementProviderCustomerConfigReducer,
  cashManagementNotification: cashManagementNotificationReducer,
  customer: customerReducer,
  circuitBreakerBlocklist: circuitBreakerBlocklistReducer,
  circuitBreakerRiskRule: persistReducer(
    circuitBreakerRiskRulesPersistConfig,
    circuitBreakerRiskRuleReducer
  ),
  riskOverview: riskOverviewReducer,
  userSettings: userSettingsReducer,
  reports: reportsReducer,

  [authApi.reducerPath]: authApi.reducer,
  [portalApi.reducerPath]: portalApi.reducer,
});

const store = configureStore({
  reducer: rootReducer,
  devTools: process.env.NODE_ENV !== "production",
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(portalApi.middleware)
      .concat(authApi.middleware)
      .concat(sagaMiddleware)
      .concat(routerMiddleware(history)),
  enhancers: [sentryReduxEnhancer],
});

setupListeners(store.dispatch);

sagaMiddleware.run(rootSaga);

export const persistor = persistStore(store, {}, () => {
  const state = store.getState();
  const userId = state.auth.userId;
  const userEmail = state.auth.username;

  if (userId) {
    Sentry.setUser({
      id: userId,
      email: userEmail ?? "",
    });
  } else {
    Sentry.setUser(null);
  }
});
export type RootState = ReturnType<typeof store.getState>;

export default store;
